import type * as React from "react";
import { cn } from "~/lib/utils";

type StatusBadgeVariant =
  | "active"
  | "confirmed"
  | "pending"
  | "completed"
  | "cancelled"
  | "refunded"
  | "paid"
  | "succeeded"
  | "failed"
  | "paused"
  | "inactive"
  | "no_availability"
  | "draft"
  | "pending_review"
  | "suspended"
  | "deleted"
  | "in_transit"
  | "open"
  | "in_progress"
  | "resolved"
  | "closed"
  | "default";

// All colours pulled from design-system tokens — no hardcoded hex/rgba.
const statusClasses: Record<StatusBadgeVariant, string> = {
  // ── Success / green ────────────────────────────────────────────────
  active:
    "border-[rgba(0,184,118,0.25)] bg-[var(--green-bg)] text-[var(--green)]",
  paid: "border-[rgba(0,184,118,0.25)] bg-[var(--green-bg)] text-[var(--green)]",
  succeeded:
    "border-[rgba(0,184,118,0.25)] bg-[var(--green-bg)] text-[var(--green)]",
  resolved:
    "border-[rgba(0,184,118,0.25)] bg-[var(--green-bg)] text-[var(--green)]",

  // ── Info / blue ────────────────────────────────────────────────────
  confirmed:
    "border-[rgba(0,115,233,0.20)] bg-[var(--blue-pale)] text-[var(--blue-dark)]",
  // Blue, not the amber it used to share with `pending`. On the earnings page
  // both appear in one column — "On hold" (pending) and "Queued for payout"
  // (in_transit) are the two sides of "has my money moved yet?", and rendering
  // them identically left the label carrying the whole distinction.
  in_transit:
    "border-[rgba(0,115,233,0.20)] bg-[var(--blue-pale)] text-[var(--blue-dark)]",

  // ── Warning / amber ────────────────────────────────────────────────
  // "open" and "in_progress" share the "Under review" label
  // (see statusLabelOverrides below) — keep them all amber for visual parity.
  open: "border-[rgba(255,140,0,0.25)] bg-[var(--amber-bg)] text-[var(--amber)]",
  pending:
    "border-[rgba(255,140,0,0.25)] bg-[var(--amber-bg)] text-[var(--amber)]",
  in_progress:
    "border-[rgba(255,140,0,0.25)] bg-[var(--amber-bg)] text-[var(--amber)]",
  pending_review:
    "border-[rgba(255,140,0,0.25)] bg-[var(--amber-bg)] text-[var(--amber)]",
  inactive:
    "border-[rgba(255,140,0,0.25)] bg-[var(--amber-bg)] text-[var(--amber)]",
  no_availability:
    "border-[rgba(255,140,0,0.25)] bg-[var(--amber-bg)] text-[var(--amber)]",

  // ── Danger / red ───────────────────────────────────────────────────
  cancelled:
    "border-[rgba(255,59,48,0.25)] bg-[var(--red-bg)] text-[var(--red)]",
  refunded:
    "border-[rgba(255,59,48,0.25)] bg-[var(--red-bg)] text-[var(--red)]",
  failed: "border-[rgba(255,59,48,0.25)] bg-[var(--red-bg)] text-[var(--red)]",
  suspended:
    "border-[rgba(255,59,48,0.25)] bg-[var(--red-bg)] text-[var(--red)]",
  deleted: "border-[rgba(255,59,48,0.25)] bg-[var(--red-bg)] text-[var(--red)]",

  // ── Neutral / ink ──────────────────────────────────────────────────
  completed: "border-[var(--ink-20)] bg-[var(--ink-10)] text-[var(--ink-60)]",
  paused: "border-[var(--ink-20)] bg-[var(--ink-10)] text-[var(--ink-60)]",
  draft: "border-[var(--ink-20)] bg-[var(--ink-10)] text-[var(--ink-60)]",
  closed: "border-[var(--ink-20)] bg-[var(--ink-10)] text-[var(--ink-60)]",
  default: "border-[var(--ink-20)] bg-[var(--ink-10)] text-[var(--ink-60)]",
};

interface StatusBadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  status: StatusBadgeVariant | string;
  label?: string;
}

const statusLabelOverrides: Partial<Record<StatusBadgeVariant, string>> = {
  // Render active in-flight ticket states as "Under review" so support-ticket
  // surfaces use a consistent label.
  open: "Under review",
  in_progress: "Under review",
};

export function StatusBadge({
  status,
  label,
  className,
  ...props
}: StatusBadgeProps) {
  const variant = (
    status in statusClasses ? status : "default"
  ) as StatusBadgeVariant;

  const resolvedLabel =
    label ?? statusLabelOverrides[variant] ?? status.replace(/_/g, " ");

  return (
    <span
      className={cn(
        "inline-flex rounded-[var(--r-full)] border px-2.5 py-0.5",
        // A pill that wraps stops reading as a pill — "QUEUED FOR PAYOUT" broke
        // across three lines in narrow table cells and swelled the row.
        "whitespace-nowrap",
        "font-ui text-[10px] font-semibold uppercase tracking-[0.06em]",
        statusClasses[variant],
        className,
      )}
      {...props}
    >
      {resolvedLabel}
    </span>
  );
}
